import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { GridComponent } from 'src/app/core/widgets/grid/grid.component';
import { GridColumn } from 'src/app/core/models/grid-column.model';
import { GridFilter } from 'src/app/core/models/grid-filter.model';
import { GridSearchFilter } from 'src/app/core/models/grid-search-filter.model';
import { take, catchError } from 'rxjs/operators';
import moment from 'moment';
import { Product } from 'src/app/order/models/product.model';
import { ProductService } from 'src/app/quoting/services/product.service';
import { AuthService } from 'src/app/auth/services/auth.service';
// Shared status token rather than a second copy of the hex. The ERP's older chart palettes fail a
// colourblind check, so the validated tokens live in one service.
import { STATUS_CRITICAL } from '../../services/warehousing-chart.service';
import { CostDisplayService, NOT_PRICED } from './cost-display.service';
import { COST_HISTORY_ENTITY, ProductCostHistoryService } from './product-cost-history.service';
import { REVALUATION_ROUTE } from '../stock-revaluation/stock-revaluation.component';

@Component({
    selector: 'app-cost-history',
    templateUrl: './cost-history.component.html',
    styleUrls: ['./cost-history.component.scss'],
    standalone: false
})
export class CostHistoryComponent implements OnInit {

  // The grid reads this.filters when it queries but has no ngOnChanges, so changing a selection
  // has to ask it to re-query.
  @ViewChild(GridComponent)
  grid: GridComponent;

  entity: string = COST_HISTORY_ENTITY;

  products: Product[] = [];

  selectedProductId: number = -1;

  /** Set when the history route does not answer at all, which is a different thing from no history. */
  isUnavailable: boolean = false;

  /** Set when the history route answers 401/403, so the screen says so rather than showing nothing. */
  isForbidden: boolean = false;

  /**
   * An audit trail, so no row buttons and nothing editable. Every money column goes through a
   * formatter rather than being rendered raw, for two reasons: the grid blanks a falsy value, so a
   * cost of 0 would otherwise vanish; and an absent cost has to read as "not priced" rather than as
   * zero, because a product nobody has priced and a product deliberately priced at nothing are
   * different facts with the same shape.
   */
  columns: GridColumn[] = [
    {active: true, label: '#', field: 'id', value: v => String(v)} as GridColumn,
    {active: true, label: 'Changed', field: 'createdAt', value: v => moment(v).format('DD/MM/YYYY HH:mm:ss')} as GridColumn,
    // The formatter takes the whole record because the grid hands a dotted field's first argument in
    // as undefined; and it reads the relation defensively because the dotted-field path in the grid
    // is not inside its own try/catch, so an absent join throws rather than blanking.
    {active: true, label: 'Product', field: 'product.name', value: (v, r) => this.productName(r)} as GridColumn,
    {active: true, label: 'SKU', field: 'product.sku', value: (v, r) => this.productSku(r)} as GridColumn,
    {active: true, label: 'Previous Cost', field: 'previousCost', value: v => this.costDisplay.cost(v)} as GridColumn,
    {active: true, label: 'New Cost', field: 'cost', value: v => this.costDisplay.cost(v)} as GridColumn,
    {active: true, label: 'Change', field: 'cost', value: (v, r) => this.changeCell(r)} as GridColumn,
    {active: true, label: 'Notes', field: 'notes', value: v => this.costDisplay.notes(v)} as GridColumn,
    {active: true, label: 'Changed By', field: 'createdBy', value: (v, r) => this.costDisplay.changedBy(r)} as GridColumn
  ];

  relationships: string[] = ['product', 'user'];

  filters: GridFilter[] = [];

  searchFields: any = ['product.name', 'product.sku', 'notes'];

  searchFilters: GridSearchFilter[] = [
    {
      field: 'product.name',
      label: 'Product',
    } as GridSearchFilter,
    {
      field: 'product.sku',
      label: 'SKU',
    } as GridSearchFilter,
    {
      field: 'notes',
      label: 'Notes',
    } as GridSearchFilter,
  ];

  constructor(private productService: ProductService,
    private productCostHistoryService: ProductCostHistoryService,
    private costDisplay: CostDisplayService,
    private authService: AuthService,
    private route: ActivatedRoute) { }

  ngOnInit(): void {
    this.loadProducts();
    this.checkHistoryAvailable();

    // Deep link for one product, matching how the products grid already links into stock levels.
    // Set here rather than after the grid exists so its first query carries the filter, which is why
    // this does not need to ask the grid to re-query.
    this.route.queryParams
    .pipe(take(1))
    .subscribe((params) => {
      if (params['productId']) {
        this.onProductChanged(+params['productId']);
      }
    })
  }

  /**
   * Whether to offer the revaluation screen from here. Hides a link the user cannot follow; see the
   * note on the component behind it for why this is a label rather than a gate.
   */
  canRevalue(): boolean {
    return this.authService.checkIfUserCanAccessRoute(REVALUATION_ROUTE);
  }

  loadProducts() {
    this.productService.getAllActiveProducts()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 403 || e.status === 401) {
        return e;
      }
      alert("Cannot load products");
      return e;
    }))
    .subscribe((data: Product[]) => {
      this.products = data;
    })
  }

  /**
   * Finds out whether there is a history table to read before the empty grid can be mistaken for an
   * empty history. A 404 means the route is not there; a 401 or 403 means it is there and this user
   * may not read it. Neither is "no cost has ever changed", and the screen says which it is.
   */
  checkHistoryAvailable() {
    this.productCostHistoryService.probe()
    .pipe(take(1))
    .pipe(catchError((e) => {
      if (e.status === 401 || e.status === 403) {
        this.isForbidden = true;
      } else {
        this.isUnavailable = true;
      }
      return e;
    }))
    .subscribe(() => {
      this.isUnavailable = false;
      this.isForbidden = false;
    })
  }

  onProductChanged(productId: number) {
    this.selectedProductId = +productId;
    this.applyFilters();
  }

  /** The product currently picked, or null while the filter is on all products. */
  getSelectedProduct(): Product {
    if (this.selectedProductId === -1) {
      return null;
    }

    return this.products.filter(p => p.id === this.selectedProductId)[0] || null;
  }

  /**
   * What the picked product costs today, shown alongside its history so the last row and the live
   * value can be compared. Reads "not priced" when the product has never been given a cost.
   */
  getSelectedProductCost(): string {
    const product = this.getSelectedProduct();

    if (!product) {
      return '';
    }

    return this.costDisplay.cost(product.cost);
  }

  isSelectedProductUnpriced(): boolean {
    const product = this.getSelectedProduct();

    return !!product && this.costDisplay.isNotPriced(product.cost);
  }

  getNotPricedLabel(): string {
    return NOT_PRICED;
  }

  /**
   * The Change cell, marked when the cost came down.
   *
   * Colour is never the only carrier of that meaning: the amount already reads "-£2.50", and a
   * downward arrow and the word "reduced" go with it. A colourblind operator, or one on a printed
   * copy, gets the same information. The status colour is applied inline rather than through a class
   * because `text-danger` is not defined in this project's stylesheet - Bootstrap is not installed -
   * so a class would have silently done nothing at all.
   */
  private changeCell(record: any): string {
    const change = this.costDisplay.change(record ? record.cost : null, record ? record.previousCost : null);

    if (!record || !this.costDisplay.isReduction(record.cost, record.previousCost)) {
      return change;
    }

    return '<span style="color: ' + STATUS_CRITICAL + '">'
      + '<i class="fas fa-arrow-down"></i> ' + change + ' reduced</span>';
  }

  private productName(record: any): string {
    const product = record ? record.product : null;

    if (product && product.name) {
      return this.costDisplay.escape(String(product.name));
    }

    // The id is still worth showing: it says which product without claiming a name for it.
    const id = Number(record ? record.productId : NaN);

    return Number.isInteger(id) && id > 0 ? 'Product #' + id : 'Unknown product';
  }

  private productSku(record: any): string {
    const product = record ? record.product : null;

    return product && product.sku ? this.costDisplay.escape(String(product.sku)) : 'No SKU';
  }

  private applyFilters() {
    const filters: GridFilter[] = [];

    if (this.selectedProductId !== -1) {
      filters.push({field: 'productId', condition: 'eq', value: this.selectedProductId} as GridFilter);
    }

    this.filters = filters;

    this.reloadGrid();
  }

  private reloadGrid() {
    if (!this.grid) {
      return;
    }

    this.grid.filters = this.filters;
    this.grid.paginationService.setPage(1);
    this.grid.newPage = 1;
    this.grid.getRecordsForEntity();
  }
}
